import {
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  CopyButton,
  Flex,
  Group,
  LoadingOverlay,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCheck, IconSearch, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Repository from "../../../_base/helper/HttpHelper";
import OpenProductTab from "../../../components/ProductTabs";
import { useProductCreateStore } from "../../../store/useProductCreateStore";
import { CategorySEO } from "./SEOModal";

interface CategorySeoSelectorProps {
  selectedIds?: number[];
  onSelectionChange?: (selectedIds: number[]) => void;
}

const CategorySeoSelector = ({
  selectedIds = [],
  onSelectionChange,
}: CategorySeoSelectorProps) => {
  const navigate = useNavigate();
  const dataItemBase = useProductCreateStore((state) => state.dataItemBase);
  const [data, setData] = useState<CategorySEO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] =
    useState<number[]>(selectedIds);
  const hacomUrl = process.env.REACT_APP_BASE_WEB_HACOM_URL;

  const formSearch = useForm({
    initialValues: {
      KeySearch: "",
    },
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const repositoryWeb = new Repository(
        process.env.REACT_APP_Demo_APP_API_URL
      );

      let url = `?Skip=0&Take=100`; // Lấy nhiều item hơn cho list
      if (formSearch.getValues().KeySearch) {
        url += `&keySearch=${formSearch.getValues().KeySearch}`;
      }

      const response = await repositoryWeb.get(
        `/TblCategoryForSeo/get-list${url}`
      );

      if (response && response.success) {
        setData(response.data || []);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching category SEO data:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (categoryId: number, checked: boolean) => {
    let newSelectedIds: number[];

    if (checked) {
      newSelectedIds = [...selectedCategoryIds, categoryId];
    } else {
      newSelectedIds = selectedCategoryIds.filter((id) => id !== categoryId);
    }

    setSelectedCategoryIds(newSelectedIds);
    onSelectionChange?.(newSelectedIds);
  };

  const handleSelectAll = () => {
    const allIds = data.map((item) => item.id);
    setSelectedCategoryIds(allIds);
    onSelectionChange?.(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedCategoryIds([]);
    onSelectionChange?.([]);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setSelectedCategoryIds(selectedIds);
  }, [selectedIds]);

  return (
    <Box
      className="flex-none"
      component="form"
      mx="10px"
      style={{ position: "relative" }}
    >
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Flex justify={"space-between"} align={"center"} my={10}>
        <CopyButton value={String(dataItemBase?.itemName)} timeout={2000}>
          {({ copied, copy }) => (
            <Tooltip
              label={copied ? "Đã sao chép" : "Sao chép"}
              withArrow
              position="right"
            >
              <Badge
                color={copied ? "teal" : "blue"}
                onClick={copy}
                style={{ cursor: "pointer" }}
                size="lg"
              >
                {dataItemBase?.itemName}
              </Badge>
            </Tooltip>
          )}
        </CopyButton>
      </Flex>

      {/* Hiển thị danh sách danh mục đã chọn */}
      <Text mt={"xs"} fw={600}>
        Hiện tại Sản phẩm đang được đặt trong các danh mục SEO sau:
      </Text>

      <Box
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "20px",
          minHeight: "40px",
          padding: "10px",
          border: "1px dashed #dee2e6",
          borderRadius: "4px",
          backgroundColor: "#f8f9fa",
        }}
      >
        {selectedCategoryIds.length === 0 ? (
          <Text size="sm" c="dimmed" style={{ fontStyle: "italic" }}>
            Chưa có danh mục SEO nào được chọn!
          </Text>
        ) : (
          data
            .filter((category) => selectedCategoryIds.includes(category.id))
            .map((category) => (
              <Box key={category.id}>
                <Text
                  style={{
                    whiteSpace: "pre",
                  }}
                >
                  <strong
                    style={{
                      color: "#3598dc",
                      fontWeight: 500,
                    }}
                  >
                    - {category.name}
                  </strong>
                </Text>
              </Box>
            ))
        )}
      </Box>

      {/* Search và actions */}
      <Group mb="md" align="flex-end">
        <TextInput
          placeholder="Tìm kiếm danh mục SEO..."
          leftSection={<IconSearch color="#15aabf" />}
          {...formSearch.getInputProps("KeySearch")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              fetchData();
            }
          }}
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Button size="sm" onClick={fetchData} variant="outline">
          Tìm kiếm
        </Button>
        <Button
          size="sm"
          onClick={handleSelectAll}
          variant="outline"
          color="blue"
          leftSection={<IconCheck size={14} />}
        >
          Chọn tất cả
        </Button>
        <Button
          size="sm"
          onClick={handleDeselectAll}
          variant="outline"
          color="red"
          leftSection={<IconX size={14} />}
        >
          Bỏ chọn tất cả
        </Button>
        <OpenProductTab
          productId={dataItemBase?.id}
          itemCode={dataItemBase?.itemName ?? ""}
          tabToOpen="info11"
          buttonText="Mở trang tab"
        />
        <Button
          color="red"
          onClick={() => {
            navigate(-1);
          }}
          leftSection={<IconX size={18} />}
        >
          Đóng
        </Button>
      </Group>

      <Text mt={"xs"} fw={600}>
        Hãy chọn danh mục SEO cho sản phẩm
      </Text>

      {/* Danh sách checkbox với style giống ItemCategoryCommandNew */}
      <Box style={{ overflowY: "auto", padding: "10px 0" }}>
        {data.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">
            Không có dữ liệu
          </Text>
        ) : (
          <Box>
            {(() => {
              // Chia danh sách data thành các nhóm 12 mục
              const chunkedData = [];
              for (let i = 0; i < data.length; i += 11) {
                chunkedData.push(data.slice(i, i + 11));
              }

              return (
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    flexWrap: "wrap",
                    gap: "20px",
                  }}
                >
                  {chunkedData.map((dataChunk, columnIndex) => (
                    <Box key={columnIndex} style={{ flex: "1 0 auto" }}>
                      {dataChunk.map((category) => (
                        <Box key={category.id} mb={5} pl={0}>
                          <Group wrap="nowrap" gap="sm">
                            <Text mr={5} mt={-3}>
                              +
                            </Text>
                            <Checkbox
                              label={
                                <Box>
                                  <Text size="sm" fw={500}>
                                    {category.name}
                                  </Text>
                                  {category.requestPath && (
                                    <Anchor
                                      href={`${hacomUrl}${category.requestPath}`}
                                      target="_blank"
                                      size="xs"
                                      c="blue"
                                      style={{ display: "block", marginTop: 2 }}
                                    >
                                      {category.requestPath}
                                    </Anchor>
                                  )}
                                  {category.metaTitle && (
                                    <Text
                                      size="xs"
                                      c="dimmed"
                                      lineClamp={1}
                                      mt={2}
                                    >
                                      Meta: {category.metaTitle}
                                    </Text>
                                  )}
                                </Box>
                              }
                              value={category.id.toString()}
                              checked={selectedCategoryIds.includes(
                                category.id
                              )}
                              onChange={(event) =>
                                handleCheckboxChange(
                                  category.id,
                                  event.currentTarget.checked
                                )
                              }
                              size="sm"
                            />
                          </Group>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </Box>
        )}
      </Box>

      {/* Footer actions giống ItemCategoryCommandNew
            <Group
                justify="flex-end"
                mt="lg"
                pt={"10px"}
                style={{
                    position: "sticky",
                    bottom: 10,
                    backgroundColor: "white",
                    borderTop: "1px solid #dee2e6",
                    paddingTop: "15px"
                }}
            >
                <Text size="sm" fw={500} mr="auto">
                    Đã chọn: {selectedCategoryIds.length} / {data.length} danh mục SEO
                </Text>
                <Button
                    type="button"
                    color="#3598dc"
                    onClick={() => {
                        // Handle save logic if needed
                        console.log("Selected category IDs:", selectedCategoryIds);
                    }}
                    leftSection={<IconCheck size={18} />}
                >
                    Lưu
                </Button>
                <Button
                    variant="outline"
                    color="black"
                    type="button"
                    onClick={() => {
                        // Handle close if needed
                    }}
                >
                    Đóng
                </Button>
            </Group> */}
    </Box>
  );
};

export default CategorySeoSelector;
